import React, { useState, useRef } from 'react';
import { ArrowLeft, Zap, DollarSign, Upload, FileImage, X, ChevronDown, Check, Clock, Settings, HardDrive, FileText, Layers, AlertCircle, Eye, Edit3 } from 'lucide-react';
import { analyzeBillWithOCR, type BillInfo } from '../OCR/index.ts';
import { MonthlyConsumptionInput } from './MonthlyConsumptionInput';
import { BILL_OCR_ENABLED } from '../constants/featureFlags';

interface SiteOperationsProps {
  onNext: (data: any) => void;
  onBack: () => void;
  initialData?: any;
}

export function SiteOperations({ onNext, onBack, initialData }: SiteOperationsProps) {
  // Monthly consumption array (12 months) – source of truth for usage
  const [monthlyConsumptionArray, setMonthlyConsumptionArray] = useState<number[]>(
    initialData?.monthlyConsumptionArray?.length === 12
      ? initialData.monthlyConsumptionArray
      : new Array(12).fill(initialData?.monthlyUsage || 0)
  );

  // Energy data state (monthlyUsage & yearlyConsumption are derived from the array)
  const [billData, setBillData] = useState({
    monthlyUsage: initialData?.monthlyUsage || 0,
    monthlyBill: initialData?.monthlyBill || 0,
    yearlyConsumption: initialData?.yearlyConsumption || 0
  });

  const [selectedUtilities, setSelectedUtilities] = useState<string[]>(initialData?.selectedUtilities || []);
  const [showUtilityDropdown, setShowUtilityDropdown] = useState(false);

  // OCR-related state
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [extractedBillInfo, setExtractedBillInfo] = useState<BillInfo | null>(null);
  const [showOCRPreview, setShowOCRPreview] = useState(false);

  // Operating hours state
  const [operatingHours, setOperatingHours] = useState<{
    shift1: { enabled: boolean; start: string; end: string; name: string };
    shift2: { enabled: boolean; start: string; end: string; name: string };
    shift3: { enabled: boolean; start: string; end: string; name: string };
    weekends: boolean;
    holidays: boolean;
  }>(initialData?.operatingHours || {
    shift1: { enabled: true, start: '08:00', end: '16:00', name: 'Day Shift' },
    shift2: { enabled: false, start: '16:00', end: '00:00', name: 'Evening Shift' },
    shift3: { enabled: false, start: '00:00', end: '08:00', name: 'Night Shift' },
    weekends: false,
    holidays: false
  });
  
  const utilityProviders = [
    'Saudi Electricity Company (SEC)',
    'National Grid SA',
    'Microgrid (Multiple System, like PV diesel)',
  ];

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<{
    bill?: File;
    datasheets: File[];
    layouts: File[];
    companyProfile?: File;
  }>({
    bill: initialData?.billFile || undefined,
    datasheets: initialData?.datasheets || [],
    layouts: initialData?.layouts || [],
    companyProfile: initialData?.companyProfileFile || undefined
  });

  const billFileRef = useRef<HTMLInputElement>(null);
  const datasheetsRef = useRef<HTMLInputElement>(null);
  const layoutsRef = useRef<HTMLInputElement>(null);
  const companyProfileRef = useRef<HTMLInputElement>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    monthlyUsage?: string;
    monthlyBill?: string;
    selectedUtilities?: string;
    billFile?: string;
    operatingHours?: string;
    datasheets?: string;
    layouts?: string;
    companyProfileFile?: string;
  }>({});

  // Validation functions
  const validateMonthlyConsumption = (values: number[]) => {
    if (!values || values.length !== 12) {
      return 'Please enter your monthly consumption for all 12 months';
    }
    const total = values.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
    if (total <= 0) {
      return 'Total annual consumption must be greater than 0';
    }
    return '';
  };

  const validateMonthlyBill = (value: number) => {
    if (value <= 0) {
      return 'Monthly bill must be greater than 0';
    }
    if (value > 1000000) {
      return 'Monthly bill seems too high. Please verify the value';
    }
    return '';
  };

  const validateUtilities = (utilities: string[]) => {
    if (utilities.length === 0) {
      return 'Please select at least one utility provider';
    }
    return '';
  };

  const validateBillFile = (file: File | undefined) => {
    if (!file) {
      return 'Please upload your electricity bill';
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PDF, JPG, or PNG file';
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      return 'File size must be less than 10MB';
    }
    return '';
  };

  const validateCompanyProfileFile = (file: File | undefined) => {
    if (!file) {
      return ''; // Company profile is optional
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PDF, DOC, or DOCX file';
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      return 'File size must be less than 10MB';
    }
    return '';
  };

  const validateOperatingHours = (hours: typeof operatingHours) => {
    const enabledShifts = Object.values(hours).filter(shift => 
      typeof shift === 'object' && 'enabled' in shift && shift.enabled
    );
    
    if (enabledShifts.length === 0) {
      return 'Please enable at least one operating shift';
    }

    // Validate shift times
    for (const [key, shift] of Object.entries(hours)) {
      if (key !== 'weekends' && key !== 'holidays' && typeof shift === 'object' && 'enabled' in shift && shift.enabled) {
        const start = new Date(`2000-01-01T${shift.start}:00`);
        const end = new Date(`2000-01-01T${shift.end}:00`);
        
        if (start >= end) {
          return `${shift.name} end time must be after start time`;
        }
      }
    }
    return '';
  };

  const validateDocumentation = (files: File[], type: string) => {
    if (files.length === 0) {
      return `Please upload at least one ${type} document`;
    }
    
    for (const file of files) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        return `All ${type} files must be PDF, JPG, or PNG format`;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        return `All ${type} files must be less than 10MB`;
      }
    }
    return '';
  };

  // OCR processing functions
  const handleOCRProcessing = async () => {
    if (!BILL_OCR_ENABLED) {
      alert('Bill OCR is temporarily disabled.');
      return;
    }
    if (!selectedFiles.bill) {
      alert('Please upload a bill file first');
      return;
    }

    setIsProcessingOCR(true);
    try {
      const result = await analyzeBillWithOCR(selectedFiles.bill);
      if (result.success && result.billInfo) {
        setExtractedBillInfo(result.billInfo);
        setShowOCRPreview(true);
      } else {
        alert('Could not extract data from the bill. Please try again or enter data manually.');
      }
    } catch (error) {
      console.error('OCR processing failed:', error);
      alert('Failed to process the bill. Please try again or enter data manually.');
    } finally {
      setIsProcessingOCR(false);
    }
  };

  const handleApplyOCRData = () => {
    if (extractedBillInfo) {
      const monthlyVal = extractedBillInfo.usage || billData.monthlyUsage;
      const newArray = new Array(12).fill(monthlyVal);
      const yearly = monthlyVal * 12;

      setMonthlyConsumptionArray(newArray);
      setBillData({
        ...billData,
        monthlyUsage: monthlyVal,
        monthlyBill: extractedBillInfo.totalAmount || billData.monthlyBill,
        yearlyConsumption: yearly
      });
      setShowOCRPreview(false);
      
      setValidationErrors(prev => ({
        ...prev,
        monthlyUsage: '',
        monthlyBill: ''
      }));
    }
  };

  const handleDiscardOCRData = () => {
    setExtractedBillInfo(null);
    setShowOCRPreview(false);
  };

  // Real-time validation function
  const validateField = (field: string, value: any) => {
    let error = '';
    
    switch (field) {
      case 'monthlyUsage':
        error = validateMonthlyConsumption(value);
        break;
      case 'monthlyBill':
        error = validateMonthlyBill(value);
        break;
      case 'selectedUtilities':
        error = validateUtilities(value);
        break;
      case 'billFile':
        error = validateBillFile(value);
        break;
      case 'companyProfileFile':
        error = validateCompanyProfileFile(value);
        break;
      case 'operatingHours':
        error = validateOperatingHours(value);
        break;
      case 'datasheets':
        error = validateDocumentation(value, 'datasheet');
        break;
      case 'layouts':
        error = validateDocumentation(value, 'layout');
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    return !error;
  };

  // File upload handlers
  const handleFileUpload = (type: 'bill' | 'datasheets' | 'layouts' | 'companyProfile', files: FileList | null) => {
    if (!files || files.length === 0) return;
    switch (type) {
      case 'bill':
        setSelectedFiles(prev => ({ ...prev, bill: files[0] }));
        validateField('billFile', files[0]);
        break;
      case 'companyProfile':
        setSelectedFiles(prev => ({ ...prev, companyProfile: files[0] }));
        validateField('companyProfileFile', files[0]);
        break;
      case 'datasheets':
      case 'layouts': {
        const newFiles = [...selectedFiles[type], ...Array.from(files)];
        setSelectedFiles(prev => ({
          ...prev,
          [type]: newFiles
        }));
        validateField(type, newFiles);
        break;
      }
    }
  };

  const removeFile = (type: 'bill' | 'datasheets' | 'layouts' | 'companyProfile', index?: number) => {
    if ((type === 'datasheets' || type === 'layouts') && typeof index === 'number') {
      const newFiles = selectedFiles[type].filter((_, i) => i !== index);
      setSelectedFiles(prev => ({
        ...prev,
        [type]: newFiles
      }));
      validateField(type, newFiles);
    } else if (type === 'companyProfile') {
      setSelectedFiles(prev => ({ ...prev, companyProfile: undefined }));
      if (companyProfileRef.current) {
        companyProfileRef.current.value = '';
      }
    } else {
      setSelectedFiles(prev => ({
        ...prev,
        [type]: type === 'bill' ? undefined : []
      }));
      validateField(type === 'bill' ? 'billFile' : type, type === 'bill' ? undefined : []);
    }
  };

  const triggerFileInput = (type: 'bill' | 'datasheets' | 'layouts' | 'companyProfile') => {
    switch (type) {
      case 'bill':
        billFileRef.current?.click();
        break;
      case 'datasheets':
        datasheetsRef.current?.click();
        break;
      case 'layouts':
        layoutsRef.current?.click();
        break;
      case 'companyProfile':
        companyProfileRef.current?.click();
        break;
    }
  };

  const toggleUtilityProvider = (provider: string) => {
    const newUtilities = selectedUtilities.includes(provider)
      ? selectedUtilities.filter(p => p !== provider)
      : [...selectedUtilities, provider];
    setSelectedUtilities(newUtilities);
    validateField('selectedUtilities', newUtilities);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate all fields
    const isMonthlyUsageValid = validateField('monthlyUsage', monthlyConsumptionArray);
    const isMonthlyBillValid = validateField('monthlyBill', billData.monthlyBill);
    const isUtilitiesValid = validateField('selectedUtilities', selectedUtilities);
    const isBillFileValid = validateField('billFile', selectedFiles.bill);
    const isOperatingHoursValid = validateField('operatingHours', operatingHours);
    const isDatasheetsValid = validateField('datasheets', selectedFiles.datasheets);
    const isLayoutsValid = validateField('layouts', selectedFiles.layouts);
    const isCompanyProfileValid = validateField('companyProfileFile', selectedFiles.companyProfile);
    
    // Check if all validations pass
    if (!isMonthlyUsageValid || !isMonthlyBillValid || !isUtilitiesValid || 
        !isBillFileValid || !isOperatingHoursValid || !isDatasheetsValid || !isLayoutsValid || !isCompanyProfileValid) {
      return;
    }

    const yearlyConsumption = monthlyConsumptionArray.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
    const derivedMonthlyUsage = yearlyConsumption / 12;
    
    onNext({
      ...billData,
      monthlyConsumptionArray,
      yearlyConsumption,
      monthlyUsage: derivedMonthlyUsage,
      selectedUtilities,
      billFile: selectedFiles.bill,
      billFiles: selectedFiles.bill ? [selectedFiles.bill] : [],
      operatingHours,
      datasheets: selectedFiles.datasheets,
      layouts: selectedFiles.layouts,
      datasheetFiles: selectedFiles.datasheets,
      layoutFiles: selectedFiles.layouts,
      companyProfileFile: selectedFiles.companyProfile
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Site & Operations</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Energy Information Section */}
          <div className="border-b pb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-blue-600" />
              Energy Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bill Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Electricity Bill
                </label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                    validationErrors.billFile
                      ? 'border-red-500 bg-red-50'
                      : selectedFiles.bill 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-blue-500'
                  }`}
                  onClick={() => !selectedFiles.bill && triggerFileInput('bill')}
                >
                  {selectedFiles.bill ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FileImage className="h-6 w-6 text-green-600 mr-3" />
                        <span className="text-green-700">{selectedFiles.bill.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile('bill');
                        }}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto h-10 w-10 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        Upload your electricity bill
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        (PDF, JPG, PNG)
                      </p>
                    </>
                  )}
                  <input
                    ref={billFileRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload('bill', e.target.files)}
                  />
                </div>
                {validationErrors.billFile && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.billFile}
                  </p>
                )}
                
                {/* OCR Functionality */}
                {selectedFiles.bill && (
                  <div className="mt-4">
                    {BILL_OCR_ENABLED ? (
                      <button
                        type="button"
                        onClick={handleOCRProcessing}
                        disabled={isProcessingOCR}
                        className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {isProcessingOCR ? 'Processing...' : 'Extract Data from Bill'}
                      </button>
                    ) : (
                      <p className="text-sm text-gray-500">OCR extraction is temporarily disabled.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Usage and Bill Data */}
              <div className="space-y-4">
                <div>
                  <MonthlyConsumptionInput
                    initialValues={monthlyConsumptionArray}
                    onValuesChange={(values) => {
                      setMonthlyConsumptionArray(values);
                      const yearly = values.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
                      setBillData(prev => ({
                        ...prev,
                        monthlyUsage: yearly / 12,
                        yearlyConsumption: yearly
                      }));
                      if (validationErrors.monthlyUsage) {
                        setValidationErrors(prev => ({ ...prev, monthlyUsage: '' }));
                      }
                    }}
                    yearlyTotal={billData.yearlyConsumption}
                  />
                  {validationErrors.monthlyUsage && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.monthlyUsage}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Bill (SAR)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      required
                      className={`block w-full pl-10 rounded-lg shadow-sm focus:ring-blue-500 ${
                        validationErrors.monthlyBill ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'
                      }`}
                      placeholder="Enter monthly bill"
                      value={billData.monthlyBill}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setBillData({
                          ...billData,
                          monthlyBill: value
                        });
                        validateField('monthlyBill', value);
                      }}
                    />
                  </div>
                  {validationErrors.monthlyBill && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.monthlyBill}
                    </p>
                  )}
                </div>

                {/* Utility Providers */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Utility Providers (Select Multiple)
                  </label>
                <div 
                  className="relative"
                >
                    <button
                      type="button"
                      className={`w-full bg-white border rounded-lg px-4 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors.selectedUtilities 
                          ? 'border-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                      onClick={() => setShowUtilityDropdown(!showUtilityDropdown)}
                      onMouseEnter={() => setShowUtilityDropdown(true)}
                      onMouseLeave={() => {
                        // Only close if not hovering over the dropdown
                        setTimeout(() => {
                          if (!showUtilityDropdown) {
                            setShowUtilityDropdown(false);
                          }
                        }, 100);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">
                          {selectedUtilities.length === 0 
                            ? 'Select utility providers...' 
                            : `${selectedUtilities.length} provider(s) selected`
                          }
                        </span>
                        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                          showUtilityDropdown ? 'transform rotate-180' : ''
                        }`} />
                      </div>
                    </button>
                    
                    {showUtilityDropdown && (
                      <div 
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg"
                        onMouseEnter={() => setShowUtilityDropdown(true)}
                        onMouseLeave={() => setShowUtilityDropdown(false)}
                      >
                        {utilityProviders.map((provider) => (
                          <div
                            key={provider}
                            className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => toggleUtilityProvider(provider)}
                            onMouseEnter={() => setShowUtilityDropdown(true)}
                          >
                            <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center transition-colors ${
                              selectedUtilities.includes(provider) 
                                ? 'bg-blue-600 border-blue-600' 
                                : 'border-gray-300'
                            }`}>
                              {selectedUtilities.includes(provider) && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <span className="text-gray-700">{provider}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {selectedUtilities.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedUtilities.map((provider) => (
                        <span
                          key={provider}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {provider}
                          <button
                            type="button"
                            className="ml-2 text-blue-600 hover:text-blue-800"
                            onClick={() => toggleUtilityProvider(provider)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {validationErrors.selectedUtilities && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.selectedUtilities}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* OCR Preview Section */}
          {BILL_OCR_ENABLED && showOCRPreview && extractedBillInfo && (
            <div className="border-b pb-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Edit3 className="h-5 w-5 mr-2 text-green-600" />
                Extracted Bill Data
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Review the data extracted from your bill. You can apply it to auto-fill the form or discard it.
              </p>
              
              <div className="bg-green-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <label className="text-xs text-gray-500">Total Consumption</label>
                    <p className="font-semibold text-green-700 text-lg">
                      {extractedBillInfo.usage ? 
                        `${extractedBillInfo.usage} ${extractedBillInfo.usageUnit || 'kWh'}` : 
                        'Not found'
                      }
                    </p>
                    {extractedBillInfo.usage && (
                      <p className="text-sm text-gray-600 mt-1">
                        This will be used for monthly usage and yearly consumption calculation
                      </p>
                    )}
                  </div>
                  
                  <div className="bg-white p-3 rounded border">
                    <label className="text-xs text-gray-500">Bill Amount</label>
                    <p className="font-semibold text-green-700 text-lg">
                      {extractedBillInfo.totalAmount ? 
                        `${extractedBillInfo.totalAmount} SAR` : 
                        'Not found'
                      }
                    </p>
                    {extractedBillInfo.totalAmount && (
                      <p className="text-sm text-gray-600 mt-1">
                        This will be used for monthly bill amount
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleApplyOCRData}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Apply Data
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscardOCRData}
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Discard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Operating Hours & Schedule Section */}
          <div className="border-b pb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-600" />
              Operating Hours & Schedule
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Define your facility's operating schedule to optimize energy system design and usage patterns.
            </p>
            
            <div className="space-y-6">
              {/* Shift Configuration */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[
                  { key: 'shift1', shift: operatingHours.shift1 },
                  { key: 'shift2', shift: operatingHours.shift2 },
                  { key: 'shift3', shift: operatingHours.shift3 }
                ].map(({ key, shift }) => (
                  <div key={key} className={`border-2 rounded-lg p-4 transition-all ${
                    shift.enabled ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={shift.enabled}
                          onChange={(e) => {
                            const newHours = {
                              ...operatingHours,
                              [key]: { ...shift, enabled: e.target.checked }
                            };
                            setOperatingHours(newHours);
                            validateField('operatingHours', newHours);
                          }}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center transition-colors ${
                          shift.enabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {shift.enabled && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className={`font-medium ${shift.enabled ? 'text-blue-900' : 'text-gray-500'}`}>
                          {shift.name}
                        </span>
                      </label>
                    </div>
                    
                    <div className={`space-y-3 ${shift.enabled ? '' : 'opacity-50'}`}>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={shift.start}
                          onChange={(e) => {
                            const newHours = {
                              ...operatingHours,
                              [key]: { ...shift, start: e.target.value }
                            };
                            setOperatingHours(newHours);
                            validateField('operatingHours', newHours);
                          }}
                          disabled={!shift.enabled}
                          className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                        <input
                          type="time"
                          value={shift.end}
                          onChange={(e) => {
                            const newHours = {
                              ...operatingHours,
                              [key]: { ...shift, end: e.target.value }
                            };
                            setOperatingHours(newHours);
                            validateField('operatingHours', newHours);
                          }}
                          disabled={!shift.enabled}
                          className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center cursor-pointer p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={operatingHours.weekends}
                    onChange={(e) => {
                      const newHours = { ...operatingHours, weekends: e.target.checked };
                      setOperatingHours(newHours);
                      validateField('operatingHours', newHours);
                    }}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center transition-colors ${
                    operatingHours.weekends ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {operatingHours.weekends && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Weekend Operations</span>
                    <p className="text-sm text-gray-500">Facility operates on weekends</p>
                  </div>
                </label>

                <label className="flex items-center cursor-pointer p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={operatingHours.holidays}
                    onChange={(e) => {
                      const newHours = { ...operatingHours, holidays: e.target.checked };
                      setOperatingHours(newHours);
                      validateField('operatingHours', newHours);
                    }}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center transition-colors ${
                    operatingHours.holidays ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {operatingHours.holidays && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Holiday Operations</span>
                    <p className="text-sm text-gray-500">Facility operates during holidays</p>
                  </div>
                </label>
              </div>

              {/* Operating Hours Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  Active Schedule Summary
                </h4>
                <div className="space-y-1 text-sm text-gray-600">
                  {[
                    { key: 'shift1', shift: operatingHours.shift1 },
                    { key: 'shift2', shift: operatingHours.shift2 },
                    { key: 'shift3', shift: operatingHours.shift3 }
                  ].filter(({ shift }) => shift.enabled).map(({ key, shift }) => (
                    <div key={key} className="flex justify-between">
                      <span>{shift.name}:</span>
                      <span className="font-mono">{shift.start} - {shift.end}</span>
                    </div>
                  ))}
                  {operatingHours.weekends && <div>• Weekend operations enabled</div>}
                  {operatingHours.holidays && <div>• Holiday operations enabled</div>}
                </div>
              </div>
              {validationErrors.operatingHours && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.operatingHours}
                </p>
              )}
            </div>
          </div>

          {/* Machine Documentation & Specifications Section */}
          <div className="border-b pb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <HardDrive className="h-5 w-5 mr-2 text-blue-600" />
              Machine Documentation & Specifications
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Upload technical documentation for your industrial equipment to ensure optimal energy system integration.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Datasheets */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  Equipment Datasheets
                </h4>
                
                {selectedFiles.datasheets.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {selectedFiles.datasheets.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-blue-700 text-sm">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('datasheets', index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                    validationErrors.datasheets
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}
                  onClick={() => triggerFileInput('datasheets')}
                >
                  <FileText className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">Upload datasheets</p>
                  <p className="text-xs text-gray-500 mt-1">(PDF, DOC, XLS)</p>
                  <input
                    ref={datasheetsRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => handleFileUpload('datasheets', e.target.files)}
                  />
                </div>
                {validationErrors.datasheets && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.datasheets}
                  </p>
                )}
              </div>

              {/* Layouts */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Layers className="h-4 w-4 mr-2 text-green-600" />
                  Facility Layouts
                </h4>
                
                {selectedFiles.layouts.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {selectedFiles.layouts.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                        <div className="flex items-center">
                          <Layers className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-green-700 text-sm">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('layouts', index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                    validationErrors.layouts
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300 hover:border-green-500'
                  }`}
                  onClick={() => triggerFileInput('layouts')}
                >
                  <Layers className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">Upload layouts</p>
                  <p className="text-xs text-gray-500 mt-1">(PDF, DWG, CAD)</p>
                  <input
                    ref={layoutsRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.dwg,.dxf,.cad"
                    onChange={(e) => handleFileUpload('layouts', e.target.files)}
                  />
                </div>
                {validationErrors.layouts && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.layouts}
                  </p>
                )}
              </div>

            </div>

            {/* Documentation Summary */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <Upload className="h-4 w-4 mr-2" />
                Documentation Summary
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Datasheets:</span>
                  <span className="font-medium">{selectedFiles.datasheets.length} files</span>
                </div>
                <div className="flex justify-between">
                  <span>Layouts:</span>
                  <span className="font-medium">{selectedFiles.layouts.length} files</span>
                </div>
              </div>
            </div>
          </div>

          {/* Company Profile Section */}
          <div className="border-b pb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Company Profile (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Upload your company profile or business documentation to help us better understand your organization.
            </p>
            
            <div>
              <div
                onClick={() => triggerFileInput('companyProfile')}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                {selectedFiles.companyProfile ? (
                  <div className="flex items-center justify-center">
                    <div className="flex items-center">
                      <FileText className="h-8 w-8 text-blue-600 mr-3" />
                      <div className="text-left">
                        <p className="text-blue-700 font-medium">{selectedFiles.companyProfile.name}</p>
                        <p className="text-sm text-blue-600">
                          {(selectedFiles.companyProfile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile('companyProfile');
                      }}
                      className="ml-4 p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-10 w-10 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      Upload company profile
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      (PDF, DOC, DOCX)
                    </p>
                  </>
                )}
              </div>
              
              <input
                ref={companyProfileRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileUpload('companyProfile', e.target.files)}
              />
              
              {validationErrors.companyProfileFile && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.companyProfileFile}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={onBack}
              className="bg-gray-100 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg"
            >
              Back
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg"
            >
              Continue to Design Requirements
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
